<template>
  <el-tree class="domains-selector"
    :class="{short: short}"
    :data="tree"
    node-key="domain"
    :default-expanded-keys="expanded">
    <template v-slot="{ data: domain }">
      <span class="domain"
        :class="[selected(domain.domain) ? 'selected' : 'unselected',
          {clicked: clicked(domain.domain), leaf: domain.leaf}]">
        <span class="domain-button"
          @click.stop="select(domain.domain)">
          <span></span>
        </span>
        <span class="domain-name">{{ domain.label }}</span>
      </span>
    </template>
  </el-tree>
</template>

<script>
const ROOT_NAME = '(default)'

export const DomainsSelector = {
  name: 'DomainsSelector',

  props: {
    value: Object,
    domains: Array,
    short: Boolean,
    defaultValue: Object,
  },

  computed: {
    sortedDomains () {
      let copy = [ ...(this.domains || []) ]
      copy.sort()
      return copy
    },
    tree () {
      let index = 0
      const sub = subdomain => {
        const prefix = subdomain ? subdomain + '.' : ''
        let children = []
        while (index < this.sortedDomains.length) {
          let domain = this.sortedDomains[index]
          if (domain === subdomain) {
            index++
          } else if (domain.substr(0, prefix.length) !== prefix) {
            break
          } else {
            const next = domain.substr(prefix.length).split('.')[0]
            children.push({
              domain: prefix + next,
              label: prefix ? '.' + next : next,
              leaf: prefix + next === domain,
              children: sub(prefix + next),
            })
          }
        }
        return children
      }
      return [{
        domain: '',
        label: ROOT_NAME,
        children: sub(''),
      }]
    },
    expanded () {
      let expanded = []
      for (let domain in (this.value || {})) {
        if (!domain) continue
        const splits = domain.split('.')
        const end = domain.length - splits[splits.length - 1].length - 1
        expanded.push(domain.substr(0, end))
      }
      return expanded
    },
  },

  methods: {
    selected (name) {
      let selected = this.selectedInValue(this.value, name)
      if (selected !== null) return selected
      selected = this.selectedInValue(this.defaultValue, name)
      if (selected !== null) return selected
      return true
    },
    selectedInValue (value, name) {
      if (!value) return null
      if (name) {
        const parts = name.split('.')
        let subdomain = name
        for (let index = parts.length - 1; index >= 0; index--) {
          if (subdomain in value) {
            return value[subdomain]
          }
          const length = subdomain.length - parts[index].length - 1
          subdomain = subdomain.substr(0, length)
        }
      }
      if ('' in value) return value['']
      return null
    },
    clicked (name) {
      return this.value && name in this.value ||
        !name && !this.defaultValue
    },
    select (name) {
      let parentSelected = null
      if (name) {
        const parent = name.substr(0, name.lastIndexOf('.'))
        parentSelected = this.selectedInValue(this.value, parent)
      }
      const defaultSelected = this.selectedInValue(this.defaultValue, name)
      let emptySelected = true
      if (parentSelected != null) {
        emptySelected = parentSelected
      } else if (defaultSelected != null) {
        emptySelected = defaultSelected
      }

      let value = { ...(this.value || {}) }
      // this domain is not clicked yet, apply the reverse value
      if (!(name in value)) {
        value[name] = !emptySelected
      // this domain has the same value as the empty one, clean it
      } else if (value[name] === emptySelected) {
        delete value[name]
      // using the default value, so keep it clicked to override
      } else if (this.defaultValue && parentSelected === null) {
        value[name] = emptySelected
      // no default value, no need to keep this domain
      } else {
        delete value[name]
      }

      var domains = []
      for (const domain in value) {
        domains.push(domain)
      }
      domains.sort()

      var index = 0
      const build = previous => {
        const domain = domains[index]
        const selected = value[domain]
        if (selected === previous) {
          delete value[domain]
        }
        index++

        const prefix = domain ? domain + '.' : ''
        while (index < domains.length) {
          if (domains[index].substr(0, prefix.length) !== prefix) {
            break
          }
          build(selected)
        }
      }
      build(this.defaultValue ? null : true)

      for (let _ in value) {
        this.$emit('input', value)
        return
      }
      this.$emit('input', null)
    },
    updateSelected (value, selected) {
      if (!selected) {
        selected = {}
        for (const domain of this.domains) {
          selected[domain] = true
        }
      }
      if (!value) return selected
      const keys = [ ...Object.keys(value) ]
      keys.sort()
      let index = 0
      for (const key of keys) {
        const s = value[key]
        const prefix = key ? key + '.' : ''
        for (; index < this.domains.length; index++) {
          const domain = this.domains[index]
          if (domain === key) selected[domain] = s
          else if (domain >= prefix) break
        }
        for (let i = index; i < this.domains.length; i++) {
          const domain = this.domains[i]
          if (domain.substr(0, prefix.length) !== prefix) break
          selected[domain] = s
        }
      }
      return selected
    },
  },

  mounted () {
    this.$emit('rules', {
      validator: (rule, value, callback) => {
        let selected
        if (this.defaultValue) {
          selected = this.updateSelected(this.defaultValue, selected)
        }
        selected = this.updateSelected(value, selected)
        for (const s of Object.values(selected)) {
          if (s) return callback()
        }
        return callback(new Error('select at least one domain'))
      },
    })
  },
}

export const defaultDomains = { '': true }

export default DomainsSelector
</script>

<style lang="scss">
  @import "../element-variables.scss";

  .el-form-item__content > .domains-selector {
    padding-top: 9px;
    padding-bottom: 9px;
    border: $--border-base;
    border-radius: $--border-radius-base;
    border-color: transparent;
  }
  .el-form-item.is-error > .el-form-item__content > .domains-selector {
    border-color: $--color-danger;
    .leaf > .domain-name {
      border-bottom: 1px dotted red;
    }
  }

  .domains-selector {
    .el-tree-node__content {
      height: auto;
      & > .el-tree-node__expand-icon {
        padding: 0 4px;
      }
    }
    &:not(.short) {
      overflow: hidden;
      .el-tree-node__content {
        clear: left;
        float: left;
        padding-left: 0 !important;
      }
      .el-tree-node {
        overflow: hidden;
      }
    }
    .el-tree-node > .el-tree-node__children {
      margin-bottom: 6px;
    }
    .domain {
      font-size: $--font-size-base;
      line-height: 19px;
      cursor: pointer;
    }
    .domain-name {
      display: inline-block;
      margin-right: 6px;
    }
    .unselected > .domain-name {
      text-decoration: line-through;
    }
    .domain-button {
      margin-right: 6px;
      cursor: pointer;
      line-height: 1;
      vertical-align: middle;
      & > span {
        display: inline-block;
        position: relative;
        border-radius: $--border-radius-small;
        border: $--border-base;
        box-sizing: border-box;
        width: 15px;
        height: 15px;
        &::after {
          display: inline-block;
          width: 13px;
          // height: 16px;
          // padding-top: 50%;
          // line-height: 0;
          padding-bottom: 1px;
          position: absolute;
          text-align: center;
          position: relative;
          bottom: 2px;
          font-size: 15px;
        }
      }
    }
    .selected > .domain-button > span::after {
      content: "+";
    }
    .unselected > .domain-button > span::after {
      content: "-";
    }
    .clicked > .domain-button > span {
      background-color: $--color-primary;
      border-color: $--color-primary;
      &::after {
        color: white;
      }
    }
    // .clicked.selected > .domain-button > span {
    //   background-color: $--color-primary;
    // }
    // .clicked.unselected > .domain-button > span {
    //   background-color: $--color-danger;
    // }
  }
</style>
